import {AreaFilters, getGroupHash, getHash} from "@/elements-react/product-area/store/area-filters";
import {getLocationValues} from "@/elements-react/product-area/store/location-utils";
import {StoreSync} from "@/elements-react/product-area/store/sync";
import {
    B2cBrand,
    B2cCategory,
    B2cProductSearchPayload,
    BrandDocument,
    BrandsByCategoryDocument,
    BusinessBrand,
    CategoriesByBrandDocument,
    FilterInput,
    ProductsSearchDocument
} from "@/generated/graphql/b2c";
import {Url} from "@/helpers/url";
import {CategoryAdditionalInfo} from "@/services/categories";
import {Services} from "@/services/services";
import {LocationHook, RouteHook} from "preact-iso";
import {PaginationMaxPages, PaginationStep} from "@/util/constants.ts";
import {stockType} from "@/constants/product-stock";

export type State = {
    registryUrl: string;
    alternates: string[];
    category: CategoryAdditionalInfo;
    brand: null | B2cBrand;
    brandsByCategory: string[];
    categoriesByBrand: string[];
    groups: Record<string, FetchedState>;
    totalPages: number;
    totalItems: number;
    resultState: null | "content" | "empty";
    blank: boolean;
    more: boolean;
    loading: boolean;
    loadedProducts: B2cProductSearchPayload[];
    currentPage: number;
}

export type Context = {
    category?: string;
    brand?: string;
    search?: string
};

export type FetchedState = {
    products: B2cProductSearchPayload[];
    total: number;
    totalItems: number;
    totalPages: number;
    productsCategories: string[];
}

export class CatalogPageStore {
    protected services: Services;
    protected controller: Set<AbortController>;
    protected state: State;
    protected context: Context = {};
    protected lastGroupHash: string | null = null;
    public sync: StoreSync;

    constructor(services: Services) {
        this.services = services;
        this.controller = new Set();
        this.state = this.createEmptyState();
        this.sync = new StoreSync();

        console.log("CatalogPageStore created");
    }

    setContext(context: Context) {
        this.context = context;
    }

    load = async (params: { path: string, filter: AreaFilters, context: Context, route: RouteHook, preloadedBrandsByCategory: BusinessBrand[] | null }) => {
        const { path, filter, context, route, preloadedBrandsByCategory } = params;

        const filters = [filter];
        const groupHash = getGroupHash(filter);
        const controller = this.createController();

        const promises = [] as Array<Promise<void>>;

        this.state.loading = true;
        this.sync.notify();

        if (!this.state.registryUrl) {
            await this.loadRegistryUrl(path, route);

            promises.push(
                this.loadAlternates(this.state.registryUrl, route),
            );

            if (context.category) {
                promises.push(
                    this.loadBrandsByCategory(context.category, preloadedBrandsByCategory)
                );
            }

            if (context.brand) {
                promises.push(
                    this.loadCategoriesByBrand(context.brand)
                );
            }
        }

        for (const item of filters) {
            const hash = getHash(item);
            if (this.state.groups[hash]) {
                continue;
            }

            promises.push(
                this.loadProducts(item, context, controller, this.lastGroupHash === groupHash)
            );
        }

        await Promise.all(promises);


        this.lastGroupHash = groupHash;
        this.state.loading = false;
        this.sync.notify();
    }

    get canonicalUrl(): string {
        return this.withPageParam(this.state.registryUrl, this.state.currentPage);
    }

    get alternatesWithPage(): string[] {
        const page = this.state.currentPage;
        return (this.state.alternates || []).map((alt) =>
            this.withPageParam(alt, page)
        );
    }

    withPageParam(url: string, page: number, origin?: string) {
        try {
            const u = new URL(url, origin);
            if (!page || page <= 1) {
                u.searchParams.delete("page");
            } else {
                u.searchParams.set("page", String(page));
            }
            const isAbsolute = /^https?:\/\//i.test(url);
            return isAbsolute ? u.toString() : `${u.pathname}${u.search}${u.hash}`;
        } catch {
            if (!page || page <= 1) return url.replace(/([?&])page=\d+/, "").replace(/\?$/, "");
            return url.includes("?") ? `${url}&page=${page}` : `${url}?page=${page}`;
        }
    }

    async loadForLocation(params: { location: LocationHook, route: RouteHook, preloadedBrandsByCategory: BusinessBrand[] | null }) {
        const path = params.route.path;
        const filter = getLocationValues(params.location);
        const context = this.context;

        await this.load({
            path: path,
            filter: filter,
            context: context,
            route: params.route,
            preloadedBrandsByCategory: params.preloadedBrandsByCategory,
        });
    }

    async loadRegistryUrl(path: string, route: RouteHook) {
        const registryUrl = Url.normalizeRoute(path);
        const registryItem = await this.services.routes.resolve(registryUrl, route.params.lang)!;

        this.state.registryUrl = registryUrl;

        if (registryItem?.type === 'category') {
            if (!this.services.categories.hasAdditionalInfoItem(registryItem.id)) {
                await this.services.routes.resolve(registryUrl, route.params.lang, true);
            }

            await this.loadCategory(registryItem.id);
        }

        if (registryItem?.type === 'brand') {
            await this.loadBrand(registryItem.id);
        }
    }

    async loadCategory(id: string) {
        const category = this.services.categories.getAdditionalInfoItemById(id)!;

        this.state.category = category;
    }

    async loadBrand(id: string) {
      const response = await this.services.api.client.query({
        query: BrandDocument,
        variables: {
          input: {
            brandId: id,
          }
        },
      });

      this.state.brand = response.data.Brand as B2cBrand;
    }

    async loadAlternates(registryUrl: string, route: RouteHook) {
        const alternates = await this.services.routes.resolveAlternates(registryUrl, route.params.lang);

        this.state.alternates = alternates;
    }

    async loadBrandsByCategory(category: string, preloadedBrandsByCategory: BusinessBrand[] | null = null) {
        let list = [];

        if (preloadedBrandsByCategory) {
            list = preloadedBrandsByCategory;
        } else {
            const response = await this.services.api.client.query({
                query: BrandsByCategoryDocument,
                variables: {
                    input: {
                        categoryId: category,
                    }
                },
            });

            list = response.data.BrandsByCategory as B2cBrand[];
        }

        this.state.brandsByCategory = list.map(item => item.id);
    }

    async loadCategoriesByBrand(brand: string) {
        const response = await this.services.api.client.query({
            query: CategoriesByBrandDocument,
            variables: {
                input: {
                    brandId: brand,
                }
            },
        });

        const list = response.data.CategoriesByBrand as B2cCategory[];
        const ids = list.map(item => item.id);

        this.state.categoriesByBrand = ids;
    }

    private clearGroupForHash(same: boolean, filters: AreaFilters) {
        const currentGroupHash = getGroupHash(filters);

        if (!same) {
            this.state.groups = {};
            return;
        }

        for (const key of Object.keys(this.state.groups)) {
            try {
                const parsed = JSON.parse(key) as AreaFilters;
                if (getGroupHash(parsed) === currentGroupHash) {
                    delete this.state.groups[key];
                }
            } catch {}
        }
    }

    private applyResultToState(filters: AreaFilters, result: FetchedState, totalPagesCap = PaginationMaxPages) {
        this.state.loadedProducts.length = 0;

        const limitedTotalPages = Math.min(result.totalPages, totalPagesCap);
        const normalizedPage = Math.max(1, Math.min(filters.page ?? 1, limitedTotalPages));

        this.state.groups[getHash(filters)] = result;
        this.state.totalPages = limitedTotalPages;
        this.state.currentPage = normalizedPage;
        this.state.resultState = result.products.length > 0 ? "content" : "empty";
        this.state.blank = false;
        this.state.more = limitedTotalPages > normalizedPage;
        this.state.totalItems = result.totalItems;

        for (const product of result.products) {
            this.state.loadedProducts.push(product);
        }
    }

    async loadProducts(filters: AreaFilters, context: Context, controller: AbortController, same = true) {
        const requestedPage = filters.page ?? 1;

        if (requestedPage > PaginationMaxPages) {
            this.clearGroupForHash(same, filters);

            const emptyResult: FetchedState = {
                products: [],
                total: 0,
                totalItems: 0,
                totalPages: PaginationMaxPages,
                productsCategories: [],
            };

            this.state.loadedProducts.length = 0;
            this.state.groups[getHash(filters)] = emptyResult;
            this.state.totalPages = PaginationMaxPages;
            this.state.currentPage = requestedPage;
            this.state.resultState = "empty";
            this.state.blank = false;
            this.state.more = false;
            this.state.totalItems = 0;

            this.sync.notify();
            return;
        }

        const result = await this.fetchProducts(filters, context, controller);

        this.clearGroupForHash(same, filters);
        this.applyResultToState(filters, result);
    }

    release = () => {
        this.releaseControllers();
        this.state = this.createEmptyState();
    }

    createController = () => {
        const controller = new AbortController();

        this.controller.add(controller);

        return controller;
    }

    releaseControllers = () => {
        for (const controller of this.controller) {
            controller.abort();
        }

        this.controller.clear();
    }

    createEmptyState = () => {
        return {
            registryUrl: null!,
            alternates: null!,
            category: null!,
            brand: null,
            brandsByCategory: null!,
            categoriesByBrand: null!,
            groups: {},
            totalPages: 0,
            resultState: null,
            blank: true,
            more: false,
            loading: false,
            loadedProducts: [],
            currentPage: 1,
        } as State;
    }

    result() {
        return this.state;
    }

    async fetchProducts(state: AreaFilters, context: Context, controller?: AbortController): Promise<FetchedState> {
        let response = null;

        if (this.services.preloaded.productsSearch) {
            response = {
                data: {
                    ProductsSearch: this.services.preloaded.productsSearch
                }
            }
            this.services.preloaded.productsSearch = null;
        } else {
            const sortBy = state.sort!;
            const priceMin = state.priceMin;
            const priceMax = state.priceMax;
            const search = context.search ?? "";
            const rawPage = state.page ?? 1;
            const safePage = Math.max(1, Math.min(rawPage, PaginationMaxPages));

            const {
                categories,
                brands
            } = this.getLists(state, context);

            const price = [
                this.getPriceFilter("=>", "price.min", priceMin)!,
                this.getPriceFilter("<=", "price.max", priceMax)!,
            ].filter(Boolean);

            response = await this.services.api.client.query({
                query: ProductsSearchDocument,
                variables: {
                    input: {
                        pageSize: PaginationStep,
                        page: safePage,
                        query: search,
                        sortBy: sortBy,
                        filter: [
                            { op: "=", prop: "brand", value: [...brands] },
                            { op: "=", prop: "category", value: [...categories] },
                            ...price
                        ],
                    },
                },
            }, { controller });
        }

        const records = response.data.ProductsSearch.records as B2cProductSearchPayload[];
        const products =
            records.filter((product) => product.categories.length > 0)
                .filter(
                    (product) =>
                        !this.services.customerChannel.channelOptions?.isStockOnly ||
                        [stockType.localDistributorStock, stockType.localStock].includes(product.stockType)
                );
        const total = response.data.ProductsSearch.pagination.totalPages;
        const totalItems = response.data.ProductsSearch.pagination.totalCount;
        const totalPages = response.data.ProductsSearch.pagination.totalPages;
        const productsCategories = response.data.ProductsSearch.categories.map((category) => category.id);

        return {
            products,
            total,
            totalItems,
            totalPages,
            productsCategories
        };
    }

    protected getPriceFilter(op: string, prop: string, value: null | number) {
        if (value === null) {
            return null;
        }

        const normalized = this.services.formatter.getBaseCurrencyValue(value * 100).amount;
        const fixed = (normalized * 100)!.toFixed().toString();

        return { op, prop, value: fixed } as unknown as FilterInput;
    }

    protected getLists(state: AreaFilters, context: Context) {
        const brands = state.brands.slice();
        const categories = state.categories.slice();


        if (context.category && !categories.length) {
            categories.push(context.category);
        }

        if (context.brand && !brands.length) {
            brands.push(context.brand);
        }

        return {
            brands,
            categories
        };
    }
}